import { useCallback, useState } from 'react';
import loadable from '@loadable/component';

import BinSolid from '@simplywallst/ui-core/icons/BinSolid';
import CogOutline from '@simplywallst/ui-core/icons/CogOutline';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';

import { Menu, Item } from '@/components/Menu';

import { Grid, Text, Button } from '@simplywallst/ui-core';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useIsMobileScreen } from '@/hooks/useMedia';

import type { Watchlist } from '@/hooks/useWatchlist';

const DeleteWatchlistModal = loadable(() => import('../DeleteWatchlistModal'));
const RenameWatchlistModal = loadable(() => import('../RenameWatchlistModal'));

type Options = 'rename-watchlist' | 'delete-watchlist';

interface Props {
  watchlist: Watchlist;
}

export const Settings = ({ watchlist }: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const [modal, setModal] = useState<Options | null>(null);

  const handleCloseModal = useCallback(() => setModal(null), []);

  const handleSelect = useCallback((value: string) => {
    setModal(value as Options);
  }, []);

  return (
    <>
      <Menu
        label={
          <Button
            styleType="gentle"
            alignIcon={isMobileScreen ? undefined : 'left'}
            aria-label="settings"
          >
            <CogOutline aria-label="Watchlist menu" />
            {isMobileScreen ? '' : ' Settings'}
          </Button>
        }
        placement="bottom end"
        onAction={(key) => handleSelect(String(key))}
      >
        <Item textValue="Rename watchlist" key="rename-watchlist">
          <Grid templateColumns="24px auto" alignItems="center" gap="8px">
            <EditSolid width="24px" opacity={0.5} />
            <Text typography="xSmall" color="spaceGray">
              Rename watchlist
            </Text>
          </Grid>
        </Item>
        <Item textValue="Delete watchlist" key="delete-watchlist">
          <Grid templateColumns="24px auto" alignItems="center" gap="8px">
            <BinSolid width="24px" opacity={0.5} />
            <Text typography="xSmall" color="spaceGray">
              Delete watchlist
            </Text>
          </Grid>
        </Item>
      </Menu>

      {modal === 'rename-watchlist' && (
        <RenameWatchlistModal
          watchlist={watchlist}
          onClose={handleCloseModal}
          onRename={handleCloseModal}
        />
      )}

      {modal === 'delete-watchlist' && (
        <DeleteWatchlistModal
          watchlist={watchlist}
          onClose={handleCloseModal}
          onDelete={handleCloseModal}
        />
      )}
    </>
  );
};
